import type { ErrorComponentProps } from '@tanstack/react-router'
import { Button } from '@heroui/react'
import * as Sentry from '@sentry/react'
import { rootRouteId, useMatch, useRouter } from '@tanstack/react-router'
import * as React from 'react'
import { useLocale, useTranslations } from 'use-intl'
import { FootballCoreBack } from '~/components/common/football-core-back'
import { PublicLayout } from '~/components/layout/public-layout'
import {
  footballCoreHref,
  footballCoreLocale,
} from '~/lib/navigation/football-core'
import { IntlProvider } from '../providers/intl-provider'

function DefaultCatchBoundaryBase({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  const isRoot = useMatch({ strict: false, select: state => state.id === rootRouteId })
  const locale = useLocale()
  const t = useTranslations('components.errors.serverError')
  const header = useTranslations('components.layout.header')
  const localeValue = footballCoreLocale(locale)
  const matchesPath = footballCoreHref(localeValue, '/matches')

  if (import.meta.env.DEV)
    console.warn(error)
  else
    Sentry.captureException(error)

  const handleReset = React.useCallback(() => {
    if (reset)
      reset()
    else router.invalidate()
  }, [reset, router])

  return (
    <PublicLayout>
      <div className="mx-auto flex w-full max-w-3xl flex-col px-4 py-12 text-foreground sm:px-6 sm:py-16">
        <nav data-breadcrumbs aria-label={t('breadcrumbLabel')} className="text-sm text-muted">
          <a href={matchesPath} className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg font-bold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
            {header('matches')}
          </a>
          <span aria-hidden="true" className="mx-2">/</span>
          <span aria-current="page">{t('title')}</span>
        </nav>
        <section className="mt-4 rounded-2xl border border-divider bg-content1 p-8 text-center shadow-sm" aria-labelledby="server-error-title">
          <h1 id="server-error-title" className="text-5xl font-extrabold text-danger">{t('title')}</h1>
          <p className="mt-4 text-base leading-7 text-muted">{t('description')}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button className="min-h-11" variant="primary" onPress={handleReset}>{t('tryAgain')}</Button>
            {isRoot
              ? (
                  <a href={matchesPath} className="inline-flex min-h-11 items-center justify-center rounded-xl border border-divider px-4 font-bold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                    {t('goHome')}
                  </a>
                )
              : (
                  <FootballCoreBack
                    className="inline-flex min-h-11 items-center justify-center rounded-xl border border-divider px-4 font-bold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    fallback={matchesPath}
                    locale={localeValue}
                  >
                    {t('goBack')}
                  </FootballCoreBack>
                )}
          </div>
        </section>
      </div>
    </PublicLayout>
  )
}

export function DefaultCatchBoundary({ error, reset }: ErrorComponentProps) {
  return (
    <IntlProvider>
      <DefaultCatchBoundaryBase error={error} reset={reset} />
    </IntlProvider>
  )
}
